import { useState, FormEvent, ChangeEvent } from 'react';
import formValidationController from '../controllers/formValidationController';
import { languageController } from '../controllers/language/languageController';

// Validation pattern types
type ValidationPattern = 
  | 'required'
  | 'name'
  | 'nameMaxMin'
  | 'postalcode'
  | 'email'
  | 'phone'
  | 'isAlphaNumeric'
  | 'vin'
  | 'odometer'
  | 'positive';

interface FormData {
  [key: string]: string | number | boolean;
}

interface FormErrors {
  [key: string]: string | undefined;
}

interface Validations {
  [fieldName: string]: ValidationPattern[];
}

interface UseFormProps {
  initState: FormData;
  validations?: Validations;
  onSubmit?: () => void;
}

interface UseFormReturn {
  data: FormData;
  handleChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  errors: FormErrors;
  setData: React.Dispatch<React.SetStateAction<FormData>>;
}

// Custom hook to handle general form actions
export const useForm = ({
  initState,
  validations,
  onSubmit
}: UseFormProps): UseFormReturn => {
  const [data, setData] = useState<FormData>(initState);
  const [errors, setErrors] = useState<FormErrors>({});

  // Handle form element changes
  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setData({
      ...data,
      [name]: value,
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validations) {
      let valid = true;
      const newErrors: FormErrors = {};

      for (const element in validations) {
        const value = data[element];
        const patterns = validations[element];
        const isRequired = patterns.includes('required');

        // If it's not 'required' and value is empty, skip the validations on it.
        if (!isRequired && !value) continue;

        // Loop through validations on that element.
        for (const pattern of patterns) {
          // Terminate the loop as soon as the current validation in the loop is false. 
          if (pattern && !formValidationController.validateField(value, pattern)) {
            valid = false;
            newErrors[element] = languageController.getError(pattern);
            break;
          }
        }
      }

      if (!valid) {
        setErrors(newErrors);
        return;
      }
    }

    // Initialize states
    setErrors({});

    // Run onSubmit after validation
    if (onSubmit) {
      const submitButton = e.currentTarget.querySelector<HTMLButtonElement>('button[type="submit"]');
      if (submitButton) {
        submitButton.disabled = true;
      }
      onSubmit();
    }
  };

  return {
    data,
    handleChange,
    handleSubmit,
    errors,
    setData
  };
};
